import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2026-09-22 08:06:59 +0200 (Tue, 22 Sep 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 165808 $
//  Purpose   : Navigation Menu for presentations
// =============================================================================
// ---Third-party imports
import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Progress } from 'semantic-ui-react';
// ---Framework imports
import { Sel } from 'uix/registries';
import { VIcon, Link } from 'uix/components';
import { useSelector } from 'uix/hooks';
import { WikiPane } from 'uix/wiki/render';
import { usePresLanguage } from '../hooks';
// ---Local imports
import S from './navigationMenu.css';
import { log } from 'uix/logging';
// ---Globals
const LanguageSwitcher = ({ enableLang })=>{
    let i18n = useSelector(Sel.i18n);
    let { presLang, selected, switchLanguage } = usePresLanguage(enableLang);
    if (!presLang || presLang.length <= 1) return null;
    let choices = presLang.map((lang)=>/*#__PURE__*/ _jsx("button", {
            type: "button",
            className: clsx(lang === selected && 'selected'),
            style: {
                appearance: 'none',
                background: 'none',
                border: 0,
                font: 'inherit',
                marginLeft: '1rem',
                cursor: 'pointer',
                color: lang === selected ? 'var(--presentation_themeColor)' : 'var(--presentation_meta_contentColor)'
            },
            "aria-pressed": lang === selected,
            onClick: ()=>switchLanguage(lang),
            title: i18n.get(`lang_${lang}`, lang.toUpperCase()),
            "aria-label": i18n.get(`lang_${lang}`, lang.toUpperCase()),
            children: lang.toUpperCase()
        }, lang));
    return /*#__PURE__*/ _jsx("span", {
        id: "languageSwitcher",
        children: choices
    });
};
const NavEntry = ({ caption, active, onClick, meta, href })=>{
    return /*#__PURE__*/ _jsxs("a", {
        href: href,
        style: {
            position: 'relative'
        },
        onClick: (event)=>{
            event.preventDefault();
            onClick(event);
        },
        "aria-current": active ? 'true' : undefined,
        className: clsx(active && S.active),
        children: [
            caption,
            /*#__PURE__*/ _jsx("div", {
                style: {
                    '--presentation_themeColor': meta?.themeColor,
                    background: 'var(--presentation_themeColor)'
                }
            })
        ]
    });
};
export const NavigationMenu = ({ mobile, menuContent, slides, activeSlide, setActiveSlide, progressPercent, progressAbsolute, logoPosition = 'left', parentPresId, rootNode, onOpenChange })=>{
    let apiurl = useSelector(Sel.apiurl);
    let [open, setOpen] = useState(false);
    let [disableTransition, setDisableTransition] = useState(false);
    let navRef = useRef(null);
    // Close menu when clicking/tapping outside navigation container
    useEffect(()=>{
        const handleOutside = (e)=>{
            if (!open) return;
            if (navRef.current && navRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('click', handleOutside, true);
        document.addEventListener('touchstart', handleOutside, true);
        return ()=>{
            document.removeEventListener('click', handleOutside, true);
            document.removeEventListener('touchstart', handleOutside, true);
        };
    }, [
        open
    ]);
    // logo is first image webresource with a 'logo' in the tree node caption
    let logoAsset = rootNode?.assets?.find((e)=>e.datatype == 9 && e.subtype == 2 && e.caption?.toLowerCase().includes('logo'));
    let parentLogoAsset = rootNode?.assets?.find((e)=>e.datatype == 9 && e.subtype == 2 && e.caption?.toLowerCase().includes('home'));
    let styles = {};
    // scroll behavior for auto-hiding the navigation menu
    let [navVisible, setNavVisible] = useState(true);
    let [isHovered, setIsHovered] = useState(false);
    // y buffer on mouse-leave: pointer below the bar within the buffer still counts as hovering
    let HOVER_LEAVE_BUFFER = 80;
    let hoverLeaveCleanup = useRef(null);
    useEffect(()=>()=>hoverLeaveCleanup.current?.(), []);
    let setHovered = (h)=>{
        hoverLeaveCleanup.current?.();
        hoverLeaveCleanup.current = null;
        setIsHovered(h);
    };
    let onNavMouseLeave = (e)=>{
        let rect = navRef.current?.getBoundingClientRect();
        if (!rect || e.clientY < rect.bottom || e.clientY > rect.bottom + HOVER_LEAVE_BUFFER) {
            setHovered(false);
            return;
        }
        let onMove = (ev)=>{
            let r = navRef.current?.getBoundingClientRect();
            if (r && (ev.clientY > r.bottom + HOVER_LEAVE_BUFFER || ev.clientY < r.top)) setHovered(false);
        };
        hoverLeaveCleanup.current?.();
        hoverLeaveCleanup.current = ()=>document.removeEventListener('mousemove', onMove);
        document.addEventListener('mousemove', onMove);
    };
    useEffect(()=>{
        // on mobile the nav never hides
        let val = mobile || !!(open || navVisible || isHovered);
        onOpenChange?.(val);
    }, [
        mobile,
        open,
        navVisible,
        onOpenChange,
        isHovered
    ]);
    let lastProgressRef = useRef(progressAbsolute);
    let isInitialJump = useRef(true);
    useEffect(()=>{
        // Disable transition during mode switch, then re-enable after all renders
        setDisableTransition(true);
        setNavVisible(true);
        let timer = setTimeout(()=>setDisableTransition(false), 600);
        return ()=>clearTimeout(timer);
    }, [
        mobile
    ]);
    useEffect(()=>{
        let diff = progressAbsolute - lastProgressRef.current;
        // ignore the initial jump after mounting (when measuring slides causes a large progress change)
        if (isInitialJump.current && Math.abs(diff) > 0) {
            isInitialJump.current = false;
            lastProgressRef.current = progressAbsolute;
            return;
        }
        if (diff > 150) {
            // scrolling down fast: hide nav
            setNavVisible(false);
            lastProgressRef.current = progressAbsolute;
        } else if (diff < -50) {
            // scrolling up: show nav
            setNavVisible(true);
            lastProgressRef.current = progressAbsolute;
        } else if (progressAbsolute <= 100) {
            // at the very top: show nav
            setNavVisible(true);
            lastProgressRef.current = progressAbsolute;
        }
    }, [
        progressAbsolute
    ]);
    let navHidden = !mobile && !(navVisible || open || isHovered);
    let navClasses = clsx(S.navigationContainer, navHidden && S.hideNav, disableTransition && S.noTransition);
    let menuItems;
    let rightMenuItems;
    let leftMenuItems;
    let hamburgerMenuItems;
    if (menuContent && menuContent.$children) {
        let { $children } = menuContent;
        let menuItemChildren = $children.filter((i)=>i.$tag === 'MenuItem');
        menuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position !== 'right' && i.position !== 'left' && i.position !== 'hamburger')
        };
        rightMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'right')
        };
        leftMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'left')
        };
        hamburgerMenuItems = {
            ...menuContent,
            $children: menuItemChildren.filter((i)=>i.position === 'hamburger')
        };
    }
    // build room navigation from slides with isRoomSlide prop
    let roomNavEntries = slides.filter((slide)=>{
        let roomHideInMenu = slide.meta?.roomHideInMenu;
        const isHidden = roomHideInMenu !== undefined ? roomHideInMenu : slide.meta?.hideInMenu;
        /* hideInMenu old meta support */ return slide.isRoomSlide && !isHidden;
    }).map(({ caption, otid, index, meta })=>/*#__PURE__*/ _jsx(NavEntry, {
            style: {
                '--presentation_themeColor': meta.themeColor,
                background: 'var(--presentation_themeColor)'
            },
            meta: meta,
            caption: caption,
            href: `#${otid}`,
            active: slides[activeSlide]?.room?.otid == otid,
            onClick: ()=>setActiveSlide(index)
        }, otid));
    let backButton = parentPresId ? /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(Link, {
            className: clsx(S.backButton, parentLogoAsset && S.logo),
            id: parentPresId,
            title: "zurück zur Ausstellung",
            children: parentLogoAsset ? /*#__PURE__*/ _jsx("img", {
                alt: "Home Logo",
                src: apiurl.href(`/domainimage/${parentLogoAsset.otid}`)
            }) : /*#__PURE__*/ _jsx(VIcon, {
                name: "level up alternate"
            })
        })
    }) : null;
    let logoLink = logoAsset ? /*#__PURE__*/ _jsx("a", {
        href: `#${slides[0]?.otid || ''}`,
        className: S.logo,
        onClick: (event)=>{
            event.preventDefault();
            setActiveSlide(0);
        },
        children: /*#__PURE__*/ _jsx("img", {
            alt: "Zum Anfang der Präsentation",
            src: apiurl.href(`/domainimage/${logoAsset.otid}`)
        })
    }) : null;
    let hasMenuItems = menuItems?.$children?.length > 0;
    let hasHamburgerItems = hamburgerMenuItems?.$children?.length > 0;
    let hasRightMenuItems = rightMenuItems?.$children?.length > 0;
    let hasRoomNav = roomNavEntries?.length > 0;
    let mobileMenuContent = hasMenuItems ? /*#__PURE__*/ _jsx(WikiPane, {
        rendered: menuItems
    }) : hasRoomNav ? roomNavEntries : hasHamburgerItems && /*#__PURE__*/ _jsx(WikiPane, {
        rendered: hamburgerMenuItems
    });
    let hamburgerVisible = mobile ? !!mobileMenuContent || hasRightMenuItems : hasHamburgerItems;
    if (mobile) {
        return /*#__PURE__*/ _jsxs("div", {
            ref: navRef,
            style: styles,
            className: navClasses,
            onMouseEnter: ()=>setHovered(true),
            onMouseLeave: onNavMouseLeave,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx('presNavigation', S.navigation, S.mobile),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: clsx(S.nav),
                            children: [
                                backButton,
                                logoLink,
                                leftMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                    rendered: leftMenuItems
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: ()=>setOpen(open ? false : true),
                                    "aria-expanded": open,
                                    "aria-label": open ? 'Menü schließen' : 'Menü öffnen',
                                    className: clsx(S.hamburger, open && S.open, !hamburgerVisible && S.hidden),
                                    children: /*#__PURE__*/ _jsxs("span", {
                                        className: S.lines,
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {}),
                                            /*#__PURE__*/ _jsx("span", {}),
                                            /*#__PURE__*/ _jsx("span", {})
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("nav", {
                            onClick: ()=>setOpen(false),
                            className: clsx(S.mobileMenu, open && S.open, S.nav),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: S.mainMenuContent,
                                    children: mobileMenuContent
                                }),
                                (hasMenuItems || hasRoomNav) && hasHamburgerItems && /*#__PURE__*/ _jsx("div", {
                                    className: S.hamburgerItems,
                                    children: /*#__PURE__*/ _jsx(WikiPane, {
                                        rendered: hamburgerMenuItems
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: S.bottom,
                                    children: [
                                        hasRightMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: rightMenuItems
                                        }),
                                        /*#__PURE__*/ _jsx(LanguageSwitcher, {
                                            enableLang: rootNode.meta?.languages
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Progress, {
                    className: clsx(S.progressBar, '.presProgressBar'),
                    percent: progressPercent,
                    attached: "top",
                    "aria-hidden": "true"
                })
            ]
        });
    } else {
        return /*#__PURE__*/ _jsxs("div", {
            style: styles,
            className: navClasses,
            onMouseEnter: ()=>setHovered(true),
            onMouseLeave: onNavMouseLeave,
            onFocus: ()=>setHovered(true),
            onBlur: (event)=>{
                if (!event.currentTarget.contains(event.relatedTarget)) setHovered(false);
            },
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx('presNavigation', S.navigation),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: S.navRow,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.nav, S.left),
                                    children: [
                                        backButton,
                                        logoPosition == 'left' && logoLink,
                                        leftMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: leftMenuItems
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("nav", {
                                    className: clsx(S.nav),
                                    children: [
                                        logoPosition == 'center' && logoLink,
                                        hasMenuItems ? /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: menuItems
                                        }) : hasRoomNav && roomNavEntries
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.nav, S.right),
                                    children: [
                                        hasRightMenuItems && /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: rightMenuItems
                                        }),
                                        logoPosition == 'right' && logoLink,
                                        /*#__PURE__*/ _jsx(LanguageSwitcher, {
                                            enableLang: rootNode.meta?.languages
                                        })
                                    ]
                                })
                            ]
                        }),
                        hasHamburgerItems && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: clsx(S.navigation),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: clsx(S.nav),
                                            children: /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                ref: navRef,
                                                onClick: ()=>setOpen(!open),
                                                "aria-expanded": open,
                                                "aria-label": open ? 'Menü schließen' : 'Menü öffnen',
                                                className: clsx(S.hamburger, open && S.open),
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    className: S.lines,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {}),
                                                        /*#__PURE__*/ _jsx("span", {}),
                                                        /*#__PURE__*/ _jsx("span", {})
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Progress, {
                                            className: clsx(S.progressBar, '.presProgressBar'),
                                            percent: progressPercent,
                                            attached: "top",
                                            "aria-hidden": "true"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("nav", {
                                    onClick: ()=>setOpen(false),
                                    style: disableTransition ? {
                                        display: 'none'
                                    } : undefined,
                                    className: clsx(S.mobileMenu, open && S.open, S.nav),
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: S.hamburgerItems,
                                        children: /*#__PURE__*/ _jsx(WikiPane, {
                                            rendered: hamburgerMenuItems
                                        })
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Progress, {
                    className: S.progressBar,
                    percent: progressPercent,
                    attached: "top",
                    "aria-hidden": "true"
                })
            ]
        });
    }
};
